import { createAnimationChannel, type AnimationChannel } from "../../animation/animationChannel";
import { BotClient, createBrowserBotWorker } from "../../bot";
import { MatchController } from "../../features/match/controller";
import type { MatchHistoryPort } from "../../features/match/persistence";
import {
  createWebHapticFeedback,
  createWebSoundFeedback,
  type HapticFeedbackPort,
  type SoundFeedbackPort,
} from "../feedback";
import { createWebLifecyclePort, type LifecyclePort } from "../lifecycle";
import { createMatchOwnershipPort } from "../ownership";
import { createIndexedDbMatchStorage } from "../persistence";
import {
  createWebSettingsStore,
  DEFAULT_SETTINGS,
  type SettingsStore,
} from "../settings/settingsStore";

export type AppRuntime = Readonly<{
  controller: MatchController;
  animation: AnimationChannel;
  settingsStore: SettingsStore;
  sound: SoundFeedbackPort;
  haptics: HapticFeedbackPort;
  lifecycle: LifecyclePort;
  history: MatchHistoryPort;
  dispose(): void;
}>;

export function createBrowserAppRuntime(): AppRuntime {
  const animation = createAnimationChannel();
  const sound = createWebSoundFeedback({ enabled: DEFAULT_SETTINGS.soundEnabled });
  const haptics = createWebHapticFeedback({ enabled: DEFAULT_SETTINGS.hapticsEnabled });
  const bot = new BotClient(createBrowserBotWorker);
  const storage = createIndexedDbMatchStorage();
  const ownership = createMatchOwnershipPort();
  const controller = new MatchController({
    bot,
    animation: animation.port,
    storage,
    ownership,
  });
  const settingsStore = browserSettingsStore();
  const lifecycle = createWebLifecyclePort();
  let disposed = false;

  return Object.freeze({
    controller,
    animation,
    settingsStore,
    sound,
    haptics,
    lifecycle,
    history: storage,
    dispose() {
      if (disposed) return;
      disposed = true;
      controller.dispose();
      void sound.dispose();
      void haptics.dispose();
    },
  });
}

function browserSettingsStore(): SettingsStore {
  try {
    return createWebSettingsStore(globalThis.localStorage);
  } catch {
    return Object.freeze({
      load: () =>
        Promise.resolve({
          kind: "default" as const,
          settings: DEFAULT_SETTINGS,
          reason: "unavailable" as const,
        }),
      save: () => Promise.resolve({ kind: "unavailable" as const }),
      clear: () => Promise.resolve({ kind: "unavailable" as const }),
    });
  }
}
